import React, { useId } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import { WEB_PLATFORM } from '../../constants';
import { getPlatformDisplayName } from '../../utils';
import { getDraftPlatformSelection, STORE_SELECTION_PLATFORMS } from '../appCardUtils';
import { HelpLabel, PlatformIcon } from '../controls';
import css from '../AppCard.css';
import { platformPropType } from './propTypes';

const PlatformSelector = ({
    isPublished,
    platformName,
    platformNames,
    platforms,
    readOnly,
    selectedBucketPlatform,
    showSelectionMethod,
    siteId,
    onSitePatch,
}) => {
    const selectionMethodId = useId();

    return (
        <>
            <div className={css.platformPicker}>
                <div className={css.sectionLabel}>
                    <Translate id="STATIC.PAGES.APPS_CONFIGURATION.OS_SELECTION_LABEL" />
                </div>
                <div className={css.platformChoices}>
                    {platformNames.map(platform => (
                        <button
                            key={platform}
                            type="button"
                            disabled={readOnly || !!siteId}
                            className={classNames(css.platformChoice, {
                                [css.platformChoiceActive]: selectedBucketPlatform === platform,
                            })}
                            onClick={() => {
                                const nextSelection = getDraftPlatformSelection(platforms, platform);
                                onSitePatch({
                                    platform: nextSelection.platform,
                                    os: nextSelection.os,
                                    _published: STORE_SELECTION_PLATFORMS.includes(platform),
                                });
                            }}
                        >
                            <PlatformIcon site={platform} muted={selectedBucketPlatform !== platform} />
                            {getPlatformDisplayName(platform)}
                        </button>
                    ))}
                </div>
            </div>

            {showSelectionMethod ? (
                <div className={css.selectionMethod}>
                    <HelpLabel
                        id="STATIC.PAGES.APPS_CONFIGURATION.SITE_SELECTION_METHOD_LABEL"
                        tooltip="If the site is live in the appstore, use the store URL to import data. For APK and 3rd party stores select the Bundle ID option."
                    />
                    <label htmlFor={`${selectionMethodId}-store-url`}>
                        <input
                            id={`${selectionMethodId}-store-url`}
                            type="radio"
                            checked={isPublished}
                            disabled={readOnly}
                            onChange={() => onSitePatch({ _published: true, longname: '' })}
                        />
                        {platformName === WEB_PLATFORM ? (
                            <Translate id="STATIC.PAGES.APPS_CONFIGURATION.URL_INPUT_LABEL" />
                        ) : (
                            <Translate id="STATIC.PAGES.APPS_CONFIGURATION.STORE_URL" />
                        )}
                    </label>
                    <label htmlFor={`${selectionMethodId}-bundle-id`}>
                        <input
                            id={`${selectionMethodId}-bundle-id`}
                            type="radio"
                            checked={!isPublished}
                            disabled={readOnly}
                            onChange={() => onSitePatch({ _published: false, url: '' })}
                        />
                        <Translate id="STATIC.PAGES.APPS_CONFIGURATION.BUNDLE_ID_NO_COLON" />
                    </label>
                </div>
            ) : null}
        </>
    );
};

PlatformSelector.propTypes = {
    isPublished: PropTypes.bool.isRequired,
    onSitePatch: PropTypes.func.isRequired,
    platformName: PropTypes.string.isRequired,
    platformNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    platforms: PropTypes.arrayOf(platformPropType).isRequired,
    readOnly: PropTypes.bool.isRequired,
    selectedBucketPlatform: PropTypes.string.isRequired,
    showSelectionMethod: PropTypes.bool.isRequired,
    siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default PlatformSelector;
